// LAYOUT DE TESTE — MOVIMENTO EM PLANILHA.
//
// Sintetico, `situacao: "teste"`, nunca atribuivel. Ele existe para exercitar o
// unico caminho de SAIDA que nenhum layout real cobre: `formato: "planilha"`.
//
// O adaptador devolve CELULAS, e nunca bytes. Quem transforma linhas x colunas
// em .xlsx e' a borda (`apps/api/src/_shared/planilha.ts`), e e' assim que o
// ADR-0007 se mantem de pe: o dominio nao importa SheetJS, nao conhece zip e
// nao escreve arquivo. O que ele garante e' o CONTEUDO — e conteudo, aqui, e'
// uma matriz de texto.
//
// TRES COISAS QUE A DECLARACAO DELE PROMETE, E QUE O CODIGO CUMPRE:
//
// 1. UMA LINHA POR CONTRATO, com a ADF na coluna `Referencia`. E' o que faz o
//    retorno do mes seguinte casar exato em vez de cair no fallback por
//    matricula, verba e valor. Somar por (matricula, verba) — o que a ALECE
//    obriga hoje — perde a ADF, e a perda vira ambiguidade no mes seguinte.
//
// 2. `campos-ausentes: nenhum`. Toda coluna do modelo neutro tem lugar aqui,
//    inclusive o tipo de movimento (`I`/`A`/`E`). Um layout de saida sem coluna
//    de tipo so' consegue anunciar encerramento por OMISSAO; este consegue os
//    dois, e por isso nao precisa recusar remessa nenhuma.
//
// 3. `casoVazio: "so-cabecalho"`. Competencia sem divida nenhuma sai com o
//    cabecalho e zero linhas — e com um aviso dizendo isso. Arquivo de zero
//    bytes seria indistinguivel de falha de geracao, e "so' o cabecalho" e' o
//    que o importador do ente entende como "nada a descontar este mes".
import { resumirPorCredor } from "../../movimento-modelo.js";
import { CONTEXTO_SAIDA_VAZIO, } from "../adaptador.js";
import { layoutDoCatalogo } from "../catalogo.js";
import { EXEMPLO_REMESSA, registrarExemplo } from "../exemplos.js";
import { escreverNumero } from "../forma/numero.js";
import { apenasDigitos } from "../identidade.js";
import { ABA_MOVIMENTO, COLUNAS_TESTE_MOVIMENTO } from "./planilha-colunas.js";
const CODIGO = "teste-planilha-movimento";
/** `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet` — o xlsx aberto. */
export const CONTENT_TYPE_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
/**
 * Ponto decimal, sem separador de milhar.
 *
 * A planilha guarda numero como texto sem locale; o milhar so' apareceria se
 * a Atlas o escrevesse, e escrever "1,234.56" numa celula que o importador vai
 * ler como numero e' pedir para ele achar duas colunas onde ha uma.
 */
const VALOR = { decimal: "ponto", milhar: "nenhum", sinal: "prefixo", casas: 2 };
function versao() {
    return layoutDoCatalogo(CODIGO).versao;
}
/** O cabecalho, na ordem declarada — a identidade da coluna e' o NOME. */
function cabecalho() {
    return COLUNAS_TESTE_MOVIMENTO.map((c) => c.nome);
}
/** CPF com onze digitos, sem mascara. Vazio quando a remessa nao o tem. */
function cpfOnzeDigitos(bruto) {
    const d = apenasDigitos(bruto);
    return d === "" ? "" : d.padStart(11, "0");
}
/** Primeiro dia da competencia, em `AAAA-MM-DD`. */
function mesReferencia(competencia) {
    return `${competencia.slice(0, 4)}-${competencia.slice(4, 6)}-01`;
}
function linhaDe(d, entrada, ctx) {
    const valores = {
        Matricula: d.matricula,
        CPF: cpfOnzeDigitos(d.cpf),
        Nome: d.nome ?? "",
        Verba: d.codigoVerba ?? "",
        Descricao: ctx.descricaoDaVerba(d.codigoVerba ?? "") || (d.credorNome ?? ""),
        Valor: escreverNumero(d.valorParcela, VALOR),
        Parcela: String(d.parcelaNumero),
        Total: String(d.parcelasTotal),
        // A ADF. E' esta coluna que o retorno tem de devolver.
        Referencia: d.adf,
        Movimento: d.tipoMovimento ?? "I",
        MesReferencia: mesReferencia(entrada.competencia),
        DataCorte: entrada.dataCorte,
    };
    return COLUNAS_TESTE_MOVIMENTO.map((c) => valores[c.nome] ?? "");
}
export const adaptadorTestePlanilhaMovimento = {
    codigo: CODIGO,
    serializar(entrada, ctx) {
        const celulas = [cabecalho()];
        const camposSemCorrespondencia = new Set();
        for (const d of entrada.descontos) {
            if (!d.nome)
                camposSemCorrespondencia.add("nome");
            if (!d.cpf)
                camposSemCorrespondencia.add("cpf");
            celulas.push(linhaDe(d, entrada, ctx));
        }
        const avisos = [];
        if (entrada.descontos.length === 0) {
            // O `casoVazio: "so-cabecalho"` declarado no catalogo, em palavras: a
            // planilha sai com a linha de cabecalho e nenhuma linha de dado.
            avisos.push(`a competência ${entrada.competencia} está sem dívidas: a planilha sai só com o cabeçalho, e nenhuma linha de desconto`);
        }
        return {
            arquivos: [
                {
                    nome: this.nomeArquivo(entrada),
                    // Planilha nao tem texto: a borda serializa `celulas` em xlsx.
                    texto: "",
                    codificacao: "utf-8",
                    contentType: CONTENT_TYPE_XLSX,
                    formato: "planilha",
                    celulas,
                },
            ],
            relatorio: {
                layout: { codigo: CODIGO, versao: versao() },
                competencia: entrada.competencia,
                linhas: entrada.descontos.length,
                dividasInstruidas: entrada.descontos.length,
                // Uma linha por contrato: nunca ha divida somada, e por isso a ADF
                // sempre cabe no arquivo.
                dividasSomadas: [],
                identificadorEscrito: true,
                camposSemCorrespondencia: [...camposSemCorrespondencia],
                totaisPorCredor: resumirPorCredor(entrada.descontos),
                avisos,
            },
        };
    },
    /** `MOVIMENTO-AAAAMM-DEFINITIVO.xlsx`, ou `-PREVIA` enquanto o corte não passou. */
    nomeArquivo(entrada) {
        const marca = entrada.congelada === false ? "PREVIA" : "DEFINITIVO";
        return `MOVIMENTO-${entrada.competencia}-${marca}.xlsx`;
    },
};
registrarExemplo(CODIGO, () => {
    const g = adaptadorTestePlanilhaMovimento.serializar(EXEMPLO_REMESSA, CONTEXTO_SAIDA_VAZIO);
    return {
        descricao: "O movimento de duas dívidas numa planilha de uma aba, com a ADF na coluna Referencia.",
        competencia: EXEMPLO_REMESSA.competencia,
        arquivos: g.arquivos.map((a) => ({ nome: a.nome, aba: ABA_MOVIMENTO, celulas: a.celulas ?? [] })),
    };
});
//# sourceMappingURL=planilha-movimento.js.map